<script setup>
import 'devextreme-vue/text-area'
import { ref, watch } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useColumns } from '@/composables/garage/service-history-grid'
import { useWebSiteStore } from '@/stores/web-site.store'

import { garageApi } from '@/apis/garage.api'

import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import ServiceHistoryRowGrid from '@/components/garage/ServiceHistoryRowGrid.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import MechanicsAvatarList from '@/components/shared/MechanicsAvatarList.vue'

const props = defineProps({
  vehicle: {
    type: Object,
    default: null
  },
  options: {
    type: Object,
    default: null
  }
})

const gridId = 'service-history-grid'

const webSiteStore = useWebSiteStore()

const { gridRef } = dataGrid.useGridRef()
const { route, router } = dataGrid.useGridRouter()
watch(() => webSiteStore.userDataFilter, () => gridRef.value.instance.refresh())

defineExpose({ focusedRow: dataGrid.focusedRow })
const currentCountryId = ref(null)
const currentStateId = ref(null)
const editors = {}

const columns = useColumns(editors, currentCountryId)

const onDetailContentReady = (e) => {
  gridRef.value.instance.updateDimensions()
}

let gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(garageApi.getAbsoluteUrl() + garageApi.worksheetsUrl, {
    filter: props?.vehicle?.vin ? ['vehicle_vin', '=', props.vehicle.vin] : null,
    customParamsFn: () => { return { service_history: true } }
  }),
  height: props.options?.height || '100%',
  onToolbarPreparing: (e) => e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem),
  columns: columns,
})

gridOptions = {...gridOptions, ...{
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  routerFilter: {
    fields: [{vin: 'vehicle_vin'}],
    getRoute: () => route,
    router: router
  }
}}

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #master-detail="{ data: worksheet }">
        <service-history-row-grid
          :data="worksheet"
          @content-ready="onDetailContentReady"
        />
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          show-clear-button
          :data="cellInfo"
        />
      </template>
      <template #countryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :is-portal="['customer.vat_country'].includes(cellInfo.column?.dataField)"
        />
      </template>
      <template #cityEditor="{ data: cellInfo }">
        <city-drop-down-box
          :data="cellInfo"
          :state="currentStateId?.value"
          :country="currentCountryId?.value"
        />
      </template>
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :data="cellInfo"
        />
      </template>
      <template #mechanicsAvatarList="{ data }">
        <MechanicsAvatarList :ids="data.value" />
      </template>
    </dx-data-grid>
  </div>
</template>
